import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from '../api/client'
import type {
  AIUsageResponse,
  DailyAIPoint,
  FunnelResponse,
  PaywallResponse,
  JobRunRecord,
  JobsResponse,
  TimeToMasteryResponse,
} from '../types'

// ── API helpers ───────────────────────────────────────────────────────────────

async function fetchFunnel(start: string, end: string): Promise<FunnelResponse> {
  const { data } = await api.get<FunnelResponse>('/api/v1/admin/analytics/funnel/', {
    params: { start_date: start, end_date: end },
  })
  return data
}

async function fetchAIUsage(start: string, end: string): Promise<AIUsageResponse> {
  const { data } = await api.get<AIUsageResponse>('/api/v1/admin/analytics/ai-usage/', {
    params: { start_date: start, end_date: end },
  })
  return data
}

async function fetchPaywall(start: string, end: string): Promise<PaywallResponse> {
  const { data } = await api.get<PaywallResponse>('/api/v1/admin/analytics/paywall/', {
    params: { start_date: start, end_date: end },
  })
  return data
}

async function fetchJobs(): Promise<JobsResponse> {
  const { data } = await api.get<JobsResponse>('/api/v1/admin/jobs/')
  return data
}

async function fetchTimeToMastery(start: string, end: string): Promise<TimeToMasteryResponse> {
  const { data } = await api.get<TimeToMasteryResponse>(
    '/api/v1/admin/analytics/time-to-mastery/',
    { params: { start_date: start, end_date: end } },
  )
  return data
}

// ── Date helpers ──────────────────────────────────────────────────────────────

function computeDateRange(days: number): { start: string; end: string } {
  const end = new Date()
  const start = new Date()
  start.setDate(start.getDate() - days + 1)
  const fmt = (d: Date) => d.toISOString().split('T')[0]
  return { start: fmt(start), end: fmt(end) }
}

// ── Section wrapper ───────────────────────────────────────────────────────────

interface SectionProps {
  title: string
  isLoading: boolean
  isError: boolean
  children: React.ReactNode
}

function Section({ title, isLoading, isError, children }: SectionProps) {
  return (
    <section style={{ marginBottom: 48 }}>
      <h2 style={sectionHeadingStyle}>{title}</h2>
      {isLoading && <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Loading…</p>}
      {isError && <p style={{ color: 'var(--danger)', fontSize: 14 }}>Failed to load.</p>}
      {!isLoading && !isError && children}
    </section>
  )
}

// ── Stat card ─────────────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  sub,
  highlight,
}: {
  label: string
  value: string | number
  sub?: string
  highlight?: boolean
}) {
  return (
    <div style={{
      backgroundColor: 'var(--bg-surface)',
      border: `1px solid ${highlight ? 'var(--accent)' : 'var(--border)'}`,
      borderRadius: 12,
      padding: '20px 24px',
      minWidth: 140,
      flex: '1 1 140px',
    }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color: highlight ? 'var(--accent)' : 'var(--text-primary)', lineHeight: 1 }}>
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }}>
          {sub}
        </div>
      )}
    </div>
  )
}

// ── Funnel section ────────────────────────────────────────────────────────────

function FunnelSection({ data }: { data: FunnelResponse }) {
  const conversionRate = data.registrations > 0
    ? ((data.trials_converted / data.registrations) * 100).toFixed(1)
    : '—'

  const steps = [
    { label: 'Registrations', value: data.registrations },
    { label: 'Trials started', value: data.trials_started },
    { label: 'Pro conversions', value: data.trials_converted, highlight: true },
    { label: 'Churned', value: data.churned },
  ]

  return (
    <>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        {steps.map((s, i) => (
          <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 12, flex: '1 1 auto' }}>
            <StatCard label={s.label} value={s.value} highlight={s.highlight} />
            {i < steps.length - 1 && (
              <span style={{ fontSize: 20, color: 'var(--text-secondary)', flexShrink: 0 }}>→</span>
            )}
          </div>
        ))}
      </div>
      <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
        Reg → Pro conversion rate: <strong style={{ color: 'var(--text-primary)' }}>{conversionRate}%</strong>
      </p>
    </>
  )
}

// ── AI usage section ──────────────────────────────────────────────────────────

function AIUsageSection({ data }: { data: AIUsageResponse }) {
  const totalCalls = data.total_actual + data.total_cached
  const cacheHitRate = totalCalls > 0
    ? ((data.total_cached / totalCalls) * 100).toFixed(1)
    : '—'

  const top10 = [...data.items]
    .sort((a, b) => b.total - a.total)
    .slice(0, 10)

  return (
    <>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
        <StatCard label="Provider calls" value={data.total_actual.toLocaleString()} />
        <StatCard label="Cache hits" value={data.total_cached.toLocaleString()} />
        <StatCard label="Cache hit rate" value={`${cacheHitRate}%`} highlight={parseFloat(cacheHitRate) > 40} />
        <StatCard label="Total AI requests" value={totalCalls.toLocaleString()} />
      </div>

      {top10.length > 0 && (
        <>
          <h3 style={subHeadingStyle}>Top days by usage</h3>
          <div style={tableWrapStyle}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {['Date', 'Provider calls', 'Cache hits', 'Total'].map((h) => (
                    <th key={h} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {top10.map((row: DailyAIPoint, i) => (
                  <tr key={row.date} style={{ borderBottom: i < top10.length - 1 ? '1px solid var(--border)' : 'none' }}>
                    <td style={tdStyle}>{row.date}</td>
                    <td style={tdStyle}>{row.actual}</td>
                    <td style={tdStyle}>{row.cached}</td>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{row.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {data.items.length === 0 && (
        <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>No AI calls in this period.</p>
      )}
    </>
  )
}

// ── Paywall section ───────────────────────────────────────────────────────────

function PaywallSection({ data }: { data: PaywallResponse }) {
  // Aggregate by endpoint
  const byEndpoint = data.items.reduce<Record<string, number>>((acc, item) => {
    acc[item.endpoint] = (acc[item.endpoint] ?? 0) + item.count
    return acc
  }, {})

  const sorted = Object.entries(byEndpoint).sort(([, a], [, b]) => b - a)

  return (
    <>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
        <StatCard label="Total paywall hits" value={data.total.toLocaleString()} />
      </div>

      {sorted.length > 0 && (
        <>
          <h3 style={subHeadingStyle}>By endpoint</h3>
          <div style={tableWrapStyle}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {['Endpoint', 'Hits'].map((h) => (
                    <th key={h} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map(([endpoint, count], i) => (
                  <tr key={endpoint} style={{ borderBottom: i < sorted.length - 1 ? '1px solid var(--border)' : 'none' }}>
                    <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 13 }}>{endpoint}</td>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {data.total === 0 && (
        <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>No paywall encounters in this period.</p>
      )}
    </>
  )
}

// ── Plausible section ─────────────────────────────────────────────────────────

const PLAUSIBLE_EVENTS = [
  { name: 'landing_demo_used', desc: 'Landing page demo form submitted' },
  { name: 'register_started', desc: 'User arrived at /register (once per session)' },
  { name: 'register_completed', desc: 'Registration form submitted successfully' },
  { name: 'onboarding_completed', desc: 'Onboarding form submitted' },
  { name: 'first_card_studied', desc: 'First correct answer ever (lifetime, per user)' },
  { name: 'day_1_return', desc: 'User returns 20–48 h after signup' },
  { name: 'day_7_return', desc: 'User returns 6–14 days after signup' },
]

function PlausibleSection() {
  return (
    <div style={{
      backgroundColor: 'var(--bg-surface)',
      border: '1px solid var(--border)',
      borderRadius: 12,
      padding: 24,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, marginBottom: 16 }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>
          Plausible funnel events
        </span>
        <a
          href="/plausible"
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontSize: 13, color: 'var(--accent)', fontWeight: 600 }}
        >
          Open Plausible dashboard ↗
        </a>
      </div>
      <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--text-secondary)' }}>
        The following custom events are tracked. Filter by event name in Plausible's custom events report.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {PLAUSIBLE_EVENTS.map((ev) => (
          <div key={ev.name} style={{ display: 'flex', gap: 12, alignItems: 'baseline' }}>
            <code style={{
              fontSize: 12,
              fontFamily: 'monospace',
              backgroundColor: 'var(--bg-secondary)',
              padding: '2px 8px',
              borderRadius: 4,
              color: 'var(--text-primary)',
              flexShrink: 0,
              minWidth: 200,
            }}>
              {ev.name}
            </code>
            <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{ev.desc}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Time-to-mastery section ───────────────────────────────────────────────────

function TimeToMasterySection({ data }: { data: TimeToMasteryResponse }) {
  const maxCount = Math.max(...data.buckets.map((b) => b.count), 1)

  return (
    <>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
        <StatCard label="Cards mastered" value={data.total.toLocaleString()} />
        <StatCard
          label="Median days to mastery"
          value={data.median_days !== null ? `${data.median_days.toFixed(1)}d` : '—'}
        />
      </div>

      {data.total > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {data.buckets.map((b) => {
            const widthPct = (b.count / maxCount) * 100
            return (
              <div key={b.bucket} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{
                  fontSize: 13,
                  color: 'var(--text-secondary)',
                  width: 100,
                  flexShrink: 0,
                  textAlign: 'right',
                }}>
                  {b.bucket}
                </span>
                <div style={{
                  flex: 1,
                  height: 20,
                  backgroundColor: 'var(--bg-secondary)',
                  borderRadius: 4,
                  overflow: 'hidden',
                }}>
                  <div style={{
                    height: '100%',
                    width: `${widthPct}%`,
                    backgroundColor: 'var(--accent)',
                    borderRadius: 4,
                    transition: 'width 0.3s',
                    minWidth: b.count > 0 ? 4 : 0,
                  }} />
                </div>
                <span style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'var(--text-primary)',
                  width: 48,
                  textAlign: 'right',
                  flexShrink: 0,
                }}>
                  {b.count}
                </span>
              </div>
            )
          })}
        </div>
      ) : (
        <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>No mastery milestones recorded in this period.</p>
      )}
    </>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

const PRESETS: { label: string; days: number }[] = [
  { label: '7 days', days: 7 },
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
]

export default function AdminDashboardPage() {
  const [days, setDays] = useState(7)
  const { start, end } = computeDateRange(days)

  const funnelQ = useQuery({
    queryKey: ['admin-funnel', start, end],
    queryFn: () => fetchFunnel(start, end),
  })
  const aiQ = useQuery({
    queryKey: ['admin-ai-usage', start, end],
    queryFn: () => fetchAIUsage(start, end),
  })
  const paywallQ = useQuery({
    queryKey: ['admin-paywall', start, end],
    queryFn: () => fetchPaywall(start, end),
  })
  const jobsQ = useQuery({
    queryKey: ['admin', 'jobs'],
    queryFn: fetchJobs,
    // Job records change once a day; a short stale time keeps a manual refresh
    // meaningful without polling the server.
    staleTime: 30_000,
  })
  const masteryQ = useQuery({
    queryKey: ['admin-time-to-mastery', start, end],
    queryFn: () => fetchTimeToMastery(start, end),
  })

  return (
    <div style={{ maxWidth: 1000 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 32, flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 6px' }}>
            Weekly Dashboard
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: 0 }}>
            {start} — {end}
          </p>
        </div>

        {/* Date range presets */}
        <div style={{ display: 'flex', gap: 8 }}>
          {PRESETS.map((p) => (
            <button
              key={p.days}
              onClick={() => setDays(p.days)}
              style={{
                padding: '6px 16px',
                borderRadius: 50,
                border: '1px solid var(--border)',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                backgroundColor: days === p.days ? 'var(--accent-fill)' : 'transparent',
                color: days === p.days ? 'var(--text-on-accent)' : 'var(--text-secondary)',
                transition: 'background-color 0.15s, color 0.15s',
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Pro Conversion Funnel ──────────────────────────────────────── */}
      <Section
        title="Pro Conversion Funnel"
        isLoading={funnelQ.isLoading}
        isError={funnelQ.isError}
      >
        {funnelQ.data && <FunnelSection data={funnelQ.data} />}
      </Section>

      {/* ── AI Usage ──────────────────────────────────────────────────── */}
      <Section
        title="AI Usage"
        isLoading={aiQ.isLoading}
        isError={aiQ.isError}
      >
        {aiQ.data && <AIUsageSection data={aiQ.data} />}
      </Section>

      {/* ── Paywall Encounters ────────────────────────────────────────── */}
      <Section
        title="Paywall Encounters"
        isLoading={paywallQ.isLoading}
        isError={paywallQ.isError}
      >
        {paywallQ.data && <PaywallSection data={paywallQ.data} />}
      </Section>

      {/* ── Plausible funnel events ───────────────────────────────────── */}
      <section style={{ marginBottom: 48 }}>
        <h2 style={sectionHeadingStyle}>Plausible Analytics</h2>
        <PlausibleSection />
      </section>

      {/* ── Time-to-mastery histogram ─────────────────────────────────── */}
      <Section
        title="Time to Mastery"
        isLoading={masteryQ.isLoading}
        isError={masteryQ.isError}
      >
        {masteryQ.data && <TimeToMasterySection data={masteryQ.data} />}
      </Section>

      {/* ── Scheduled jobs ────────────────────────────────────────────── */}
      <Section
        title="Scheduled Jobs"
        isLoading={jobsQ.isLoading}
        isError={jobsQ.isError}
      >
        {jobsQ.data && <JobsSection items={jobsQ.data.items} />}
      </Section>
    </div>
  )
}

// ── Scheduled jobs ────────────────────────────────────────────────────────────

const JOB_STATUS_COLOR: Record<string, string> = {
  success: 'var(--success)',
  skipped: 'var(--text-secondary)',
  failed: 'var(--danger)',
  unknown: 'var(--warning)',
}

/** Human-readable "how long ago", so a stale job is visible at a glance. */
function timeAgo(iso: string | null): string {
  if (!iso) return 'never'
  const ms = Date.now() - new Date(iso).getTime()
  if (Number.isNaN(ms)) return iso
  const mins = Math.floor(ms / 60_000)
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 48) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

/**
 * What the job did, rendered from its free-form summary.
 *
 * The reminder job gets its own sentence rather than the generic key dump. It
 * runs hourly now, so its last run legitimately reminds nobody most of the time
 * and a raw `notified_this_run: 0` reads as a dead job at a glance. The day's
 * running totals answer the question actually being asked — how many people
 * were reminded today — and the run's own numbers follow, smaller.
 */
function describeRun(item: JobRunRecord): string {
  if (item.status === 'failed') return item.error ?? 'failed'
  if (item.status === 'unknown') return 'no record — has it run since the last deploy?'
  const s = item.summary ?? {}
  if (item.job_id === 'daily_push_reminders' && s.notified_today !== undefined) {
    const parts = [
      `today: ${s.notified_today} of ${s.due_today} due reminded, over ${s.runs_today} run(s)`,
      `local window ${s.local_window}`,
      `this run: ${s.notified_this_run} of ${s.eligible_now} eligible`,
    ]
    if (Number(s.fallback_zone_today) > 0) {
      parts.push(`${s.fallback_zone_today} on the fallback zone`)
    }
    if (Number(s.deactivated_today) > 0) {
      parts.push(`${s.deactivated_today} subscription(s) retired`)
    }
    return parts.join(' · ')
  }
  const parts = Object.entries(s).map(([k, v]) => `${k}: ${v}`)
  return parts.length ? parts.join(' · ') : '—'
}

function JobsSection({ items }: { items: JobRunRecord[] }) {
  return (
    <div style={tableWrapStyle}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={thStyle}>Job</th>
            <th style={thStyle}>Last outcome</th>
            <th style={thStyle}>When</th>
            <th style={thStyle}>Result</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.job_id}>
              <td style={tdStyle}>
                <div style={{ fontWeight: 600 }}>{item.job_id}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {item.description}
                </div>
              </td>
              <td style={{ ...tdStyle, color: JOB_STATUS_COLOR[item.status] ?? 'var(--text-primary)', fontWeight: 600 }}>
                {item.status}
              </td>
              <td style={tdStyle}>{timeAgo(item.finished_at)}</td>
              <td style={{ ...tdStyle, fontSize: 13 }}>{describeRun(item)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── Style constants ───────────────────────────────────────────────────────────

const sectionHeadingStyle: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 700,
  color: 'var(--text-primary)',
  margin: '0 0 20px',
  paddingBottom: 12,
  borderBottom: '1px solid var(--border)',
}

const subHeadingStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: 'var(--text-secondary)',
  margin: '0 0 12px',
}

const tableWrapStyle: React.CSSProperties = {
  backgroundColor: 'var(--bg-surface)',
  borderRadius: 12,
  border: '1px solid var(--border)',
  overflow: 'hidden',
}

const thStyle: React.CSSProperties = {
  padding: '10px 16px',
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: 'var(--text-secondary)',
}

const tdStyle: React.CSSProperties = {
  padding: '10px 16px',
  fontSize: 14,
  color: 'var(--text-secondary)',
}
